import assert = require('assert');

import { Contract, providers, Signer } from 'ethers';
import * as TREX from '@tokenysolutions/t-rex';

import {
  connectAt,
  getTransactionOptions,
  loadContractWithCaller,
  OverrideTransactionsInterface,
  signerOrProvider,
  TransactionOptionsInterface,
} from '../utils/utils';
import {
  addAgentToToken,
  deployImplementationAuthority,
  deployToken,
  deployTokenImplementation,
  getCompliance,
  getIdentityRegistry,
  getOwnerOfImplementationAuthority,
  getOwnerOfToken,
  removeAgentFromToken,
  setCompliance,
  setIdentityRegistry,
  transferTokenOwnership,
} from '../contract-methods/token.methods';
import { deployDVD, getOwnerOfDVD, transferDVDOwnership } from '../contract-methods/dvd.methods';
import {
  addAgentToIdentityRegistry,
  deployIdentityRegistry,
  getClaimTopicsRegistry,
  getIdentityRegistryStorage,
  getOwnerOfIdentityRegistry,
  getTrustedIssuersRegistry,
  isAgent,
  removeAgentFromIdentityRegistry,
  setClaimTopicsRegistry,
  setIdentityRegistryStorage,
  setTrustedIssuersRegistry,
  transferIdentityRegistryOwnership,
} from '../contract-methods/identityRegistry.methods';
import { deployClaimTopics, getOwnerOfClaimTopicsRegistry, transferClaimTopicsRegistryOwnership } from '../contract-methods/claimTopicsRegistry.methods';
import {
  addTokenAgent,
  bindToken,
  deployCompliance,
  getOwnerOfCompliance,
  isTokenAgent,
  isTokenBound,
  removeTokenAgent,
  transferComplianceOwnership,
  unbindToken,
} from '../contract-methods/compliance.methods';
import {
  deployIdentityRegistryStorage,
  getOwnerOfIdentityRegistryStorage,
  transferIdentityRegistryStorageOwnership,
} from '../contract-methods/identityRegistryStorage.methods';
import { Role } from '../utils/enum';
import { addressesFormat, required } from '../contract-methods/decorators.methods';
import {
  deployTrustedIssuersRegistry,
  getOwnerOfTrustedIssuersRegistry,
  transferTrustedIssuersRegistryOwnership,
} from '../contract-methods/trustedIssuersRegistry.methods';

type TransactionResponse = providers.TransactionResponse;

export class Contracts {
  get trustedIssuersRegistry(): Contract {
    return this._trustedIssuersRegistry;
  }

  set trustedIssuersRegistry(value: Contract) {
    this._trustedIssuersRegistry = value;
  }

  get claimTopicsRegistry(): Contract {
    return this._claimTopicsRegistry;
  }

  get identityRegistryStorage(): Contract {
    return this._identityRegistryStorage;
  }

  get identityRegistry(): Contract {
    return this._identityRegistry;
  }

  get compliance(): Contract {
    return this._compliance;
  }

  get token(): Contract {
    return this._token;
  }

  set token(value: Contract) {
    this._token = value;
  }

  get tokenImplementation(): Contract {
    return this._tokenImplementation;
  }

  set tokenImplementation(value: Contract) {
    this._tokenImplementation = value;
  }

  get implementationAuthority(): Contract {
    return this._implementationAuthority;
  }

  set implementationAuthority(value: Contract) {
    this._implementationAuthority = value;
  }

  get dvd(): Contract {
    return this._dvd;
  }

  set dvd(value: Contract) {
    this._dvd = value;
  }

  get provider(): providers.Provider {
    return this._provider;
  }

  set provider(value: providers.Provider) {
    this._provider = value;
  }

  get signer(): Signer {
    return this._signer;
  }

  set signer(value: Signer) {
    this._signer = value;
  }

  get overrides(): OverrideTransactionsInterface {
    return this._overrides;
  }

  set overrides(value: OverrideTransactionsInterface) {
    this._overrides = value;
  }

  public _signer!: Signer;
  public _provider!: providers.Provider;
  public _token!: Contract;
  public _tokenImplementation!: Contract;
  public _implementationAuthority!: Contract;
  public _compliance!: Contract;
  public _identityRegistry!: Contract;
  public _claimTopicsRegistry!: Contract;
  public _trustedIssuersRegistry!: Contract;
  public _identityRegistryStorage!: Contract;
  public _dvd!: Contract;
  public _overrides: OverrideTransactionsInterface = {};

  /**
   * Returns an Instance of Token.
   * @returns {Contract}
   */
  public getTokenInstance(): Contract {
    return this._token;
  }

  /**
   * Returns an Instance of Token implementation.
   * @returns {Contract}
   */
  public getTokenImplementationInstance(): Contract {
    return this.tokenImplementation;
  }

  /**
   * Returns an Instance of implementation authority.
   * @returns {Contract}
   */
  public getImplementationAuthorityInstance(): Contract {
    return this.implementationAuthority;
  }

  /**
   * Returns an Instance of Compliance.
   * @returns {Contract}
   */
  public getComplianceInstance(): Contract {
    return this.compliance;
  }

  /**
   * Returns an Instance of IdentityRegistry.
   * @returns {Contract}
   */
  public getIdentityRegistryInstance(): Contract {
    return this.identityRegistry;
  }

  /**
   * Returns an Instance of DVD.
   * @returns {Contract}
   */
  public getDVDInstance(): Contract {
    return this.dvd;
  }

  /**
   * Returns an Instance of ClaimTopicsRegistry.
   * @returns {Contract}
   */
  public getClaimTopicsRegistryInstance(): Contract {
    return this.claimTopicsRegistry;
  }

  /**
   * Returns an Instance of TrustedIssuersRegistry.
   * @returns {Contract}
   */
  public getTrustedIssuersRegistryInstance(): Contract {
    return this.trustedIssuersRegistry;
  }

  /**
   * Returns an Instance of IdentityRegistryStorage.
   * @returns {Contract}
   */
  public getIdentityRegistryStorageInstance(): Contract {
    return this.identityRegistryStorage;
  }

  /**
   * Returns the address of the deployed Token smart contract.
   * @returns {string}
   */
  public getTokenAddress(): string {
    return this.token.address;
  }

  /**
   * Returns the address of the deployed Compliance smart contract.
   * @returns {string}
   */
  public getLinkedCompliance(): string {
    return this.compliance.address;
  }

  /**
   * Returns the address of the deployed DVD smart contract.
   * @returns {string}
   */
  public getDVDAddress(): string {
    return this.dvd.address;
  }

  /**
   * Returns the address of the deployed IdentityRegistry smart contract.
   * @returns {string}
   */
  public getIdentityRegistryAddress(): string {
    return this.identityRegistry.address;
  }

  /**
   * Returns the address of the deployed ClaimTopicsRegistry smart contract.
   * @returns {string}
   */
  public getClaimTopicsRegistryAddress(): string {
    return this.claimTopicsRegistry.address;
  }

  /**
   * Returns the address of the deployed TrustedIssuersRegistry smart contract.
   * @returns {string}
   */
  public getTrustedIssuersRegistryAddress(): string {
    return this.trustedIssuersRegistry.address;
  }

  /**
   * Returns the address of the deployed IdentityRegistryStorage smart contract.
   * @returns {string}
   */
  public getIdentityRegistryStorageAddress(): string {
    return this.identityRegistryStorage.address;
  }

  // DEPLOYMENT

  /**
   * Deploys Token Contract, needs IdentityRegistry and Compliance deployed first.
   * @param {{name: string; symbol: string; decimals: number; onchainID: string}} token
   * @returns {Promise<void>}
   */
  public async deployToken(token: { name: string; symbol: string; decimals: number; onchainID: string; implementationAuthority: string }): Promise<void> {
    assert(this._signer, 'Need an account');
    assert(this._identityRegistry, 'Identity Registry must be deployed first');
    assert(this._compliance, 'Compliance must be deployed first');
    this._token = await deployToken({
      implementationAuthority: token.implementationAuthority,
      identityRegistry: this._identityRegistry.address,
      compliance: this._compliance.address,
      decimals: token.decimals,
      name: token.name,
      onchainID: token.onchainID,
      symbol: token.symbol,
      signer: this._signer,
      overrides: this._overrides,
    });
  }

  /**
   * Deploys Compliance on Ethereum Blockchain
   * @returns {Promise<Contract>}
   */
  public async deployCompliance(): Promise<Contract> {
    assert(this._signer !== undefined, 'Need a Signer');
    return (this._compliance = await deployCompliance({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploys token implementation on Ethereum Blockchain
   * @returns {Promise<Contract>}
   */
  public async deployTokenImplementation(): Promise<Contract> {
    assert(this._signer !== undefined, 'Need a Signer');
    return (this._tokenImplementation = await deployTokenImplementation({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploys token implementation on Ethereum Blockchain
   * @returns {Promise<Contract>}
   */
  public async deployImplementationAuthority(implem: { tokenImplementation: string }): Promise<Contract> {
    assert(this._signer !== undefined, 'Need a Signer');
    return (this._implementationAuthority = await deployImplementationAuthority({
      tokenImplementation: implem.tokenImplementation,
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploys DVD contract on Ethereum Blockchain
   * @returns {Promise<Contract>}
   */
  public async deployDVD(): Promise<Contract> {
    assert(this._signer !== undefined, 'Need a Signer');
    return (this._dvd = await deployDVD({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploy an IdentityRegistry on Ethereum Blockchain.
   * @dev IdentityRegistryStorage, ClaimTopicsRegistry and TrustedIssuersRegistry have to be deployed first
   * @returns {Promise<Contract>}
   */
  public async deployIdentityRegistry(): Promise<Contract> {
    assert(this._signer, 'Need an account');
    assert(this._claimTopicsRegistry, 'Need Claim Topics Registry deployed');
    assert(this._trustedIssuersRegistry, 'Need Trusted Registry deployed');
    assert(this._identityRegistryStorage, 'Need Identity Registry Storage deployed');
    return (this._identityRegistry = await deployIdentityRegistry({
      TrustedIssuers: this._trustedIssuersRegistry,
      ClaimTopics: this._claimTopicsRegistry,
      IdentityRegistryStorage: this._identityRegistryStorage,
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploy an IdentityRegistryStorage on Ethereum Blockchain.
   * @returns {Promise<Contract>}
   */
  public async deployIdentityRegistryStorage(): Promise<Contract> {
    assert(this._signer !== undefined, 'Need an account');
    return (this._identityRegistryStorage = await deployIdentityRegistryStorage({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploy TrustedIssuersRegistry on Ethereum Blockchain
   * @returns {Promise<Contract>}
   */
  public async deployTrustedIssuersRegistry(): Promise<Contract> {
    assert(this._signer !== undefined, 'Need an account');

    return (this._trustedIssuersRegistry = await deployTrustedIssuersRegistry({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  /**
   * Deploys ClaimTopicsRegistry on Ethereum Blockchain.
   * @returns {Promise<Contract>}
   */
  public async deployClaimTopicsRegistry(): Promise<Contract> {
    return (this._claimTopicsRegistry = await deployClaimTopics({
      signer: this._signer,
      overrides: this._overrides,
    }));
  }

  // LOAD CONTRACTS

  /**
   * Loads Compliance
   * @param {string} contractAddress
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadCompliance(
    @required contractAddress: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._compliance = await connectAt({
      contractAddress,
      abi: TREX.contracts.DefaultCompliance.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  /**
   * Loads ImplementationAuthority
   * @param {string} contractAddress
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadImplementationAuthority(
    @required contractAddress: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._implementationAuthority = await connectAt({
      contractAddress,
      abi: TREX.contracts.ImplementationAuthority.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  /**
   * Loads DVD
   * @param {string} contractAddress
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadDVD(
    @required contractAddress: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._dvd = await connectAt({
      contractAddress,
      abi: TREX.contracts.DVDTransferManager.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  /**
   * Load an instance of IdentityRegistry
   * @dev IdentityRegistry must be deployed
   * @param {string} address
   * @returns {Promise<void>}
   */
  @addressesFormat()
  public async loadIdentityRegistry(@required address: string): Promise<Contract> {
    this._identityRegistry = await connectAt({
      contractAddress: address,
      abi: TREX.contracts.IdentityRegistry.abi,
      caller: this.signer ? this.signer : this.provider,
    });
    await this.loadClaimTopicsRegistry(await this.identityRegistry.topicsRegistry());
    await this.loadTrustedIssuersRegistry(await this.identityRegistry.issuersRegistry());
    await this.loadIdentityRegistryStorage(await this.identityRegistry.identityStorage());
    return this._identityRegistry;
  }

  /**
   * Loads IdentityRegistryStorage Instance
   * @param {string} contractAddress
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadIdentityRegistryStorage(
    @required contractAddress: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._identityRegistryStorage = await connectAt({
      contractAddress,
      abi: TREX.contracts.IdentityRegistryStorage.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  /**
   * Load an instance of TrustedIssuersRegistry
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadTrustedIssuersRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    assert(Signer.isSigner(blockchainOptions.signer) || providers.Provider.isProvider(blockchainOptions.provider), 'need at least one caller');
    return (this.trustedIssuersRegistry = await connectAt({
      contractAddress: address,
      abi: TREX.contracts.TrustedIssuersRegistry.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  /**
   * Loads ClaimTopicsRegistry
   * @param {string} contractAddress
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  @addressesFormat()
  public async loadClaimTopicsRegistry(
    @required
    contractAddress: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._claimTopicsRegistry = await connectAt({
      contractAddress,
      abi: TREX.contracts.ClaimTopicsRegistry.abi,
      caller: signerOrProvider(blockchainOptions),
    }));
  }

  // COMPLIANCE

  /**
   * Returns true if the Address is in the list of token agents
   * @param address, address of the agent
   * @param blockchainOptions
   */
  @addressesFormat()
  public async isOnTokenAgentsList(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await isTokenAgent({
      address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Returns true if the address given corresponds to a token that is bound with the Compliance contract
   * @param address, address of the token contract
   * @param blockchainOptions
   */
  @addressesFormat()
  public async isTokenBound(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await isTokenBound({
      tokenAddress: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Adds an agent to the list of token agents
   * @param address, address of the agent to be added
   * @param blockchainOptions
   */
  @addressesFormat()
  public async addAgentToAgentsList(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await addTokenAgent({
      tokenAgent: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes Agent from the list of token agents
   * @param address, address of the agent to be removed (must be added first)
   * @param blockchainOptions
   */
  @addressesFormat()
  public async removeAgentFromAgentsList(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await removeTokenAgent({
      tokenAgent: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Binds a token to the compliance contract
   * @param address, address of the token Contract to bind
   * @param blockchainOptions
   */
  @addressesFormat()
  public async bindToken(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await bindToken({
      tokenAddress: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Unbinds a token from the compliance contract
   * @param address, address of the token to unbind
   * @param blockchainOptions
   */
  @addressesFormat()
  public async unbindToken(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await unbindToken({
      tokenAddress: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  // SET NEW CONTRACTS

  /**
   * Changes Compliance from Token Contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async setCompliance(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await setCompliance({
      address: address,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   *  Changes IdentityRegistry from Token Contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async setIdentityRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await setIdentityRegistry({
      address: address,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Changes IdentityRegistryStorage from IdentityRegistry Contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async setIdentityRegistryStorage(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await setIdentityRegistryStorage({
      identityRegistryStorage: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Changes TrustedIssuersRegistry from IdentityRegistry Contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async setTrustedIssuersRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await setTrustedIssuersRegistry({
      newTrustedIssuersRegistry: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Changes ClaimTopicsRegistry from IdentityRegistry Contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async setClaimTopicsRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await setClaimTopicsRegistry({
      newClaimTopicsRegistry: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  // OWNERSHIP

  /**
   * Returns the Owner of the Token Contract.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfToken(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfToken({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Returns the Owner of the Compliance.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfCompliance(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfCompliance({
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance),
    });
  }

  /**
   * Returns the Owner of the implementation authority contract.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfImplementationAuthority(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfImplementationAuthority({
      implementationAuthority: await loadContractWithCaller(blockchainOptions, this.implementationAuthority),
    });
  }

  /**
   * Returns the Owner of the Compliance.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfDVD(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfDVD({
      dvd: await loadContractWithCaller(blockchainOptions, this.dvd),
    });
  }

  /**
   * Returns the Owner of the Identity Registry.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfIdentityRegistry(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfIdentityRegistry({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    });
  }

  /**
   * Returns the Owner of the Identity Registry Storage.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfIdentityRegistryStorage(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfIdentityRegistryStorage({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage),
    });
  }

  /**
   * Returns the Owner of the TrustedIssuers Registry.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfTrustedIssuersRegistry(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfTrustedIssuersRegistry({
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry),
    });
  }

  /**
   * Returns the Owner of the ClaimTopics Registry.
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  public async getOwnerOfClaimTopicsRegistry(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<string> {
    return await getOwnerOfClaimTopicsRegistry({
      registry: await loadContractWithCaller(blockchainOptions, this.claimTopicsRegistry),
    });
  }

  // TRANSFER OWNERSHIP

  /**
   * Transfers the Ownership of the Token to a new Owner.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferTokenOwnership(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferTokenOwnership({
      newOwner: address,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the Compliance to a new Owner.
   * @param {string} newOwner
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferComplianceOwnership(
    @required newOwner: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferComplianceOwnership({
      newOwner,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the DVD contract to a new Owner.
   * @param {string} newOwner
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferDVDOwnership(
    @required newOwner: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferDVDOwnership({
      newOwner,
      dvd: await loadContractWithCaller(blockchainOptions, this.dvd, Role.DVDOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the Identity Registry to a new Owner.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferIdentityRegistryOwnership(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferIdentityRegistryOwnership({
      newOwner: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the Identity Registry Storage to a new Owner.
   * @param {string} newOwner
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferIdentityRegistryStorageOwnership(
    @required newOwner: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferIdentityRegistryStorageOwnership({
      newOwner,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistryStorage, Role.StorageOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the Trusted Issuers Registry to a new Owner.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferTrustedIssuersRegistryOwnership(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferTrustedIssuersRegistryOwnership({
      newOwner: address,
      registry: await loadContractWithCaller(blockchainOptions, this.trustedIssuersRegistry, Role.TrustedIssuersOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Transfers the Ownership of the ClaimTopics Registry to a new Owner.
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async transferClaimTopicsRegistryOwnership(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await transferClaimTopicsRegistryOwnership({
      newOwner: address,
      registry: await loadContractWithCaller(blockchainOptions, this.claimTopicsRegistry, Role.ClaimTopicsOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  // AGENT ROLES

  /**
   * Checks if address is an Agent of the Token contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isTokenAgent(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await isAgent({
      address: address,
      registry: await loadContractWithCaller(blockchainOptions, this.token),
    });
  }

  /**
   * Checks if address is an Agent of the IdentityRegistry contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isIdentityRegistryAgent(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await isAgent({
      address: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    });
  }

  /**
   * Checks if address is an Agent of the Compliance contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<boolean>}
   */
  @addressesFormat()
  public async isComplianceAgent(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<boolean> {
    return await isTokenAgent({
      address: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Adds an Agent to the Token contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async addAgentToToken(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);

    return await addAgentToToken({
      agent: address,
      signer: this.signer,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes an Agent from the Token contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async removeAgentFromToken(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await removeAgentFromToken({
      agent: address,
      signer: this.signer,
      token: await loadContractWithCaller(blockchainOptions, this.token, Role.TokenOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Adds an Agent to the IdentityRegistry contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async addAgentToIdentityRegistry(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await addAgentToIdentityRegistry({
      agent: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes an Agent to the IdentityRegistry contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async removeAgentFromIdentityRegistry(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await removeAgentFromIdentityRegistry({
      agent: address,
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry, Role.IdentityOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Adds an Agent to the Compliance contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async addAgentToCompliance(
    @required address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await addTokenAgent({
      tokenAgent: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  /**
   * Removes an Agent to the Compliance contract
   * @param {string} address
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<TransactionResponse>}
   */
  @addressesFormat()
  public async removeAgentFromCompliance(
    @required
    address: string,
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<TransactionResponse> {
    blockchainOptions = getTransactionOptions(this, blockchainOptions);
    return await removeTokenAgent({
      tokenAgent: address,
      compliance: await loadContractWithCaller(blockchainOptions, this.compliance, Role.ComplianceOwner, this),
      overrides: blockchainOptions.overrides ?? this._overrides,
    });
  }

  // CONTRACT ADDRESS

  /**
   * Get Compliance address from Smart contracts getter
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  public async getComplianceContract(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._compliance = await getCompliance({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    }));
  }

  /**
   *  Get IdentityRegistry address from Smart contracts getter
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  public async getIdentityRegistryContract(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._identityRegistry = await getIdentityRegistry({
      token: await loadContractWithCaller(blockchainOptions, this.token),
    }));
  }

  /**
   * Get IdentityRegistryStorage address from Smart contracts getter
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  public async getIdentityRegistryStorageContract(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._identityRegistryStorage = await getIdentityRegistryStorage({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    }));
  }

  /**
   *  Get TrustedIssuersRegistry address from Smart contracts getter
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  public async getTrustedIssuersContract(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._trustedIssuersRegistry = await getTrustedIssuersRegistry({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    }));
  }

  /**
   *  Get ClaimTopicsRegistry address from Smart contracts getter
   * @param {TransactionOptionsInterface} blockchainOptions
   * @returns {Promise<Contract>}
   */
  public async getClaimTopicsContract(
    blockchainOptions: TransactionOptionsInterface = {
      signer: this.signer,
      provider: this.provider,
    },
  ): Promise<Contract> {
    return (this._claimTopicsRegistry = await getClaimTopicsRegistry({
      registry: await loadContractWithCaller(blockchainOptions, this.identityRegistry),
    }));
  }
}
